import React, { useState, useEffect, useRef } from 'react';

const Reviews = () => {
  const [selectedReview, setSelectedReview] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef(null);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);


  const clientScreenshots = [
    {
      id: 1,
      image: '/proof/upgrace_s1.jpg'
    },
    {
      id: 2,
      image: '/proof/upgrace_s2.jpg'
    },
    {
      id: 3,
      image: '/proof/upgrace_s3.jpg'
    },
    {
      id: 4,
      image: '/proof/upgrace_s4.jpg'
    },
    {
      id: 5,
      image: '/proof/upgrace_s5.jpg'
    },
    {
      id: 6,
      image: '/proof/upgrace_s6.jpg'
    },
    {
      id: 7,
      image: '/proof/upgrace_s7.jpg'
    },
    {
      id: 8,
      image: '/proof/upgrace_s8.jpg'
    },
    {
      id: 9,
      image: '/proof/upgrace_s9.jpg'
    },
    {
      id: 10,
      image: '/proof/upgrace_s10.jpg'
    },
    {
      id: 11,
      image: '/proof/upgrace_s11.jpg'
    },
    {
      id: 12,
      image: '/proof/upgrace_s12.jpg'
    },
    {
      id: 13,
      image: '/proof/upgrace_s13.jpg'
    },
    {
      id: 14,
      image: '/proof/upgrace_s14.jpg'
    },
    {
      id: 15,
      image: '/proof/upgrace_s15.jpg'
    },
    {
      id: 16,
      image: '/proof/upgrace_s16.jpg'
    },
    {
      id: 17,
      image: '/proof/upgrace_s17.jpg'
    },
    {
      id: 18,
      image: '/proof/upgrace_s18.jpg'
    },
    {
      id: 19,
      image: '/proof/upgrace_s19.jpg'
    },
    {
      id: 20,
      image: '/proof/upgrace_s20.jpg'
    },
    {
      id: 21,
      image: '/proof/upgrace_s21.jpg'
    },
    {
      id: 22,
      image: '/proof/upgrace_s22.jpg'
    },
    {
      id: 23,
      image: '/proof/upgrace_s23.jpg'
    },
    {
      id: 24,
      image: '/proof/upgrace_s24.jpg'
    }
  ];



  // Handle responsive slides count
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 576) {
        setSlidesToShow(1);
      } else if (width < 768) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play carousel
  useEffect(() => {
    const autoPlay = setInterval(() => {
      if (!isTransitioning) {
        setCurrentSlide(prev => {
          const maxSlide = Math.max(0, clientScreenshots.length - slidesToShow);
          return prev >= maxSlide ? 0 : prev + 1;
        });
      }
    }, 4000);

    return () => clearInterval(autoPlay);
  }, [isTransitioning, slidesToShow, clientScreenshots.length]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && selectedReview) {
        closeModal();
      }
    };

    if (selectedReview) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [selectedReview]);

  // Carousel navigation functions
  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    setCurrentSlide(prev => {
      const maxSlide = Math.max(0, clientScreenshots.length - slidesToShow);
      return prev >= maxSlide ? 0 : prev + 1;
    });

    setTimeout(() => setIsTransitioning(false), 300);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    setCurrentSlide(prev => {
      const maxSlide = Math.max(0, clientScreenshots.length - slidesToShow);
      return prev <= 0 ? maxSlide : prev - 1;
    });

    setTimeout(() => setIsTransitioning(false), 300);
  };


  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  const openModal = (screenshot) => {
    setSelectedReview(screenshot);

    // Analytics tracking for modal opens
    if (window.gtag) {
      window.gtag('event', 'screenshot_modal_open', {
        screenshot_id: screenshot.id
      });
    }
  };

  const closeModal = () => {
    setSelectedReview(null);
  };

  return (
    <section id="testimonials" className="section reviews-section">
      <div className="container">
        {/* Enhanced Section Header */}
        <div className="section-head">
          <h2 className="section-title">What Our Clients Say</h2>
          <p className="section-subtitle">Real feedback from satisfied clients who trusted us with their projects</p>
        </div>

        {/* Screenshots Carousel */}
        <div className="screenshots-carousel-container">
          <div
            className="screenshots-carousel"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="carousel-track"
              style={{
                transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)`,
                transition: isTransitioning ? 'transform 0.3s ease-in-out' : 'none'
              }}
            >
              {clientScreenshots.map((screenshot) => (
                <div
                  key={screenshot.id}
                  className="carousel-slide"
                  style={{ width: `${100 / slidesToShow}%` }}
                >
                  <div
                    className="screenshot-card-simple"
                    onClick={() => openModal(screenshot)}
                  >
                    <img
                      src={screenshot.image}
                      alt={`Client feedback screenshot ${screenshot.id}`}
                      className="screenshot-image-simple"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Navigation */}
          <button
            className="carousel-btn carousel-btn-prev"
            onClick={prevSlide}
            disabled={isTransitioning}
            aria-label="Previous screenshots"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <button
            className="carousel-btn carousel-btn-next"
            onClick={nextSlide}
            disabled={isTransitioning}
            aria-label="Next screenshots"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* Screenshot Modal */}
        {selectedReview && (
          <div className="modal-overlay screenshot-modal" onClick={closeModal}>
            <div className="modal-content screenshot-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>

              <div className="modal-body">
                <img
                  src={selectedReview.image}
                  alt={`Client feedback screenshot`}
                  className="modal-screenshot"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Reviews; 